import { fetchTokenMetadata, composeCosmeticPreview } from "./cosmeticCompose.js";
import { getPublicTokenImageUrl } from "./tokenImageUrl.js";

const CANVAS_SIZE = 512;

/**
 * @param {string} url
 * @param {boolean} [crossOrigin]
 */
function loadImage(url, crossOrigin = true) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image load failed: ${url}`));
    img.src = url;
  });
}

/**
 * Public PNG — `{metadataOrigin}/image/{tokenId}.png`
 * @param {string} tokenId
 * @param {number} [bust]
 */
export function owlexImageUrl(tokenId, bust) {
  const base = getPublicTokenImageUrl(tokenId);
  if (!base) return "";
  return bust != null ? `${base}?v=${bust}` : base;
}

/**
 * Same-origin proxy for canvas export (avoids missing CORS on metadata host).
 * @param {string} tokenId
 * @param {number} [bust]
 */
export function owlexImageProxyUrl(tokenId, bust) {
  const id = String(tokenId ?? "").trim();
  if (!/^\d+$/.test(id)) return "";
  const q = bust != null ? `?v=${bust}` : "";
  return `/api/owlex-image/${id}${q}`;
}

/**
 * @param {string} tokenId
 * @param {number} [bust]
 * @returns {Promise<string[]>}
 */
async function resolveOwlexImageUrls(tokenId, bust) {
  const id = String(tokenId ?? "").trim();
  if (!/^\d+$/.test(id)) return [];

  /** @type {string[]} */
  const urls = [];
  const push = (u) => {
    if (typeof u === "string" && u.length > 0 && !urls.includes(u)) urls.push(u);
  };

  push(owlexImageProxyUrl(id, bust));

  try {
    const meta = await fetchTokenMetadata(id);
    const fromMeta = String(meta?.image ?? "").trim();
    if (fromMeta) {
      push(bust != null ? `${fromMeta}${fromMeta.includes("?") ? "&" : "?"}v=${bust}` : fromMeta);
    }
  } catch {
    /* metadata optional */
  }

  push(owlexImageUrl(id, bust));
  return urls;
}

/**
 * Draw Owlex portrait: proxied PNG → metadata.image → direct PNG → layer compose from metadata.
 * @param {string} tokenId
 * @param {HTMLCanvasElement} [target]
 * @param {{ bust?: number, size?: number }} [options]
 */
export async function loadOwlexPortraitToCanvas(tokenId, target, options = {}) {
  const bust = options.bust ?? Date.now();
  const size = options.size ?? CANVAS_SIZE;
  const canvas = target ?? document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unsupported");
  ctx.clearRect(0, 0, size, size);

  const urls = await resolveOwlexImageUrls(tokenId, bust);
  for (const url of urls) {
    for (const useCors of [true, false]) {
      try {
        const img = await loadImage(url, useCors);
        ctx.drawImage(img, 0, 0, size, size);
        return canvas;
      } catch {
        /* try next */
      }
    }
  }

  const composed = await composeCosmeticPreview(tokenId, {}, canvas);
  return composed;
}

/**
 * Full-resolution portrait for trainer cards (keeps native 2000×2000 when available).
 * @param {string} tokenId
 * @param {{ bust?: number }} [options]
 * @returns {Promise<HTMLImageElement | HTMLCanvasElement>}
 */
export async function loadOwlexPortraitImage(tokenId, options = {}) {
  const bust = options.bust ?? Date.now();
  const urls = await resolveOwlexImageUrls(tokenId, bust);
  for (const url of urls) {
    for (const useCors of [true, false]) {
      try {
        return await loadImage(url, useCors);
      } catch {
        /* try next */
      }
    }
  }
  return composeCosmeticPreview(tokenId, {});
}
